import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { ThemeConfig } from '../types/theme';

interface ThemeContextType {
  themes: ThemeConfig[];
  activeTheme: ThemeConfig | null;
  createTheme: (name: string) => void;
  updateTheme: (theme: ThemeConfig) => void;
  activateTheme: (themeId: string) => void;
  deleteTheme: (themeId: string) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Aggiungiamo il defaultTheme
const defaultTheme: ThemeConfig = {
  id: 'default',
  name: 'Default Theme',
  isActive: true,
  settings: {
    typography: {
      fontFamily: 'Inter, sans-serif',
      headings: {
        desktop: {
          h1: { fontSize: '48px', fontWeight: '700', lineHeight: '1.2' },
          h2: { fontSize: '40px', fontWeight: '700', lineHeight: '1.2' },
          h3: { fontSize: '32px', fontWeight: '600', lineHeight: '1.3' },
          h4: { fontSize: '24px', fontWeight: '600', lineHeight: '1.3' },
          h5: { fontSize: '20px', fontWeight: '600', lineHeight: '1.4' },
          h6: { fontSize: '16px', fontWeight: '600', lineHeight: '1.4' }
        },
        tablet: {
          h1: { fontSize: '40px', fontWeight: '700', lineHeight: '1.2' },
          h2: { fontSize: '32px', fontWeight: '700', lineHeight: '1.2' },
          h3: { fontSize: '28px', fontWeight: '600', lineHeight: '1.3' },
          h4: { fontSize: '22px', fontWeight: '600', lineHeight: '1.3' },
          h5: { fontSize: '18px', fontWeight: '600', lineHeight: '1.4' },
          h6: { fontSize: '16px', fontWeight: '600', lineHeight: '1.4' }
        },
        mobile: {
          h1: { fontSize: '32px', fontWeight: '700', lineHeight: '1.2' },
          h2: { fontSize: '28px', fontWeight: '700', lineHeight: '1.2' },
          h3: { fontSize: '24px', fontWeight: '600', lineHeight: '1.3' },
          h4: { fontSize: '20px', fontWeight: '600', lineHeight: '1.3' },
          h5: { fontSize: '16px', fontWeight: '600', lineHeight: '1.4' },
          h6: { fontSize: '14px', fontWeight: '600', lineHeight: '1.4' }
        }
      },
      menuFont: {
        desktop: {
          fontSize: '16px',
          lineHeight: '1.5'
        },
        tablet: {
          fontSize: '15px',
          lineHeight: '1.5'
        },
        mobile: {
          fontSize: '14px',
          lineHeight: '1.5'
        },
        fontWeight: 500,
        boldWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'none',
        fontStyle: 'normal',
        fontFamily: 'Inter, sans-serif'
      },
      paragraph: {
        desktop: {
          fontSize: '16px',
          lineHeight: '1.5'
        },
        tablet: {
          fontSize: '15px',
          lineHeight: '1.5'
        },
        mobile: {
          fontSize: '14px',
          lineHeight: '1.5'
        },
        fontWeight: 400,
        boldWeight: 600,
        letterSpacing: 'normal',
        textTransform: 'none',
        fontStyle: 'normal'
      }
    },
    colors: {
      primary: '#72a853',
      secondary: '#4a6d36'
    },
    layout: {
      containerWidth: '1280px',
      spacing: '1rem'
    },
    buttons: {
      borderRadius: '0.375rem',
      padding: '0.5rem 1rem'
    },
    customCSS: ''
  }
};

function applyThemeToDocument(theme: ThemeConfig) {
  if (!theme?.settings?.typography) return;
  
  const root = document.documentElement;
  const { typography } = theme.settings;

  // Applica font family principale
  root.style.setProperty('--main-font', typography.fontFamily);

  // Applica stili paragrafo
  if (typography.paragraph) {
    const { paragraph } = typography;
    
    // Desktop
    if (paragraph.desktop) {
      root.style.setProperty('--paragraph-font-size-desktop', paragraph.desktop.fontSize);
      root.style.setProperty('--paragraph-line-height-desktop', paragraph.desktop.lineHeight);
    }
    
    // Tablet
    if (paragraph.tablet) {
      root.style.setProperty('--paragraph-font-size-tablet', paragraph.tablet.fontSize);
      root.style.setProperty('--paragraph-line-height-tablet', paragraph.tablet.lineHeight);
    }
    
    // Mobile
    if (paragraph.mobile) {
      root.style.setProperty('--paragraph-font-size-mobile', paragraph.mobile.fontSize);
      root.style.setProperty('--paragraph-line-height-mobile', paragraph.mobile.lineHeight);
    }
    
    // Proprietà comuni
    root.style.setProperty('--paragraph-font-weight', paragraph.fontWeight?.toString());
    root.style.setProperty('--paragraph-letter-spacing', paragraph.letterSpacing);
  }

  // Applica stili headings
  if (typography.headings) {
    Object.entries(typography.headings).forEach(([level, heading]) => {
      // Desktop
      if (heading.desktop) {
        root.style.setProperty(`--${level}-font-size-desktop`, heading.desktop.fontSize);
        root.style.setProperty(`--${level}-line-height-desktop`, heading.desktop.lineHeight);
      }
      
      // Tablet
      if (heading.tablet) {
        root.style.setProperty(`--${level}-font-size-tablet`, heading.tablet.fontSize);
        root.style.setProperty(`--${level}-line-height-tablet`, heading.tablet.lineHeight);
      }
      
      // Mobile
      if (heading.mobile) {
        root.style.setProperty(`--${level}-font-size-mobile`, heading.mobile.fontSize);
        root.style.setProperty(`--${level}-line-height-mobile`, heading.mobile.lineHeight);
      }
      
      // Proprietà comuni
      root.style.setProperty(`--${level}-font-weight`, heading.fontWeight?.toString());
      root.style.setProperty(`--${level}-letter-spacing`, heading.letterSpacing);
    });
  }

  console.log('✅ Tema applicato:', theme.name);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themes, setThemes] = useState<ThemeConfig[]>([]);
  const [activeTheme, setActiveTheme] = useState<ThemeConfig | null>(null);

  // Inizializza il tema predefinito
  useEffect(() => {
    console.log('🎨 Inizializzazione ThemeProvider');
    const themeRef = doc(db, 'themes', 'default');
    
    const unsubscribe = onSnapshot(themeRef, async (docSnapshot) => {
      if (!docSnapshot.exists()) {
        console.log('❌ Nessun tema trovato, creazione tema predefinito');
        try {
          // Usa setDoc invece di updateDoc per creare il documento
          await setDoc(themeRef, defaultTheme);
          console.log('✅ Tema predefinito creato');
        } catch (error) {
          console.error('❌ Errore creazione tema:', error);
        }
      } else {
        const themeData = { id: docSnapshot.id, ...docSnapshot.data() } as ThemeConfig;
        console.log('✨ Impostazione tema attivo:', themeData);
        setActiveTheme(themeData);
        setThemes([themeData]);
      }
    });

    return () => unsubscribe();
  }, []);

  // Aggiungi questo useEffect dopo applyThemeToDocument
  useEffect(() => {
    if (activeTheme) {
      console.log('🎨 Applicazione tema attivo:', activeTheme.name);
      applyThemeToDocument(activeTheme);
    }
  }, [activeTheme]);

  const createTheme = (name: string) => {
    const newTheme: ThemeConfig = {
      ...defaultTheme,
      id: crypto.randomUUID(),
      name,
      isActive: false
    };
    setThemes([...themes, newTheme]);
  };

  const updateTheme = async (theme: ThemeConfig) => {
    console.log('ThemeContext: Updating theme', theme);
    try {
      const themeRef = doc(db, 'themes', theme.id);
      const { id, ...themeWithoutId } = theme;
      await setDoc(themeRef, themeWithoutId);
      console.log('✅ Tema aggiornato con successo');
    } catch (error) {
      console.error('❌ Error updating theme:', error);
      throw error;
    }
  };

  const activateTheme = (themeId: string) => {
    setThemes(themes.map(theme => ({
      ...theme,
      isActive: theme.id === themeId
    })));
  };

  const deleteTheme = (themeId: string) => {
    setThemes(themes.filter(theme => theme.id !== themeId));
  };

  return (
    <ThemeContext.Provider value={{
      themes,
      activeTheme,
      createTheme,
      updateTheme,
      activateTheme,
      deleteTheme
    }}>
      {children}
    </ThemeContext.Provider>
  );
} 